import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { AgEditorComponent } from '../../../../global/gx-ag-grid/ag-editor.component';
import { Location } from '../../../../../models/locations-models/location';
import { ICellEditorParams } from 'ag-grid-community';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LatitudeValidators, LongitudeValidators, ValidationErrorKeys } from '../../../edit-location/service/validators';
import { takeUntil } from 'rxjs/operators';
import { CoordinateInterface } from '../../../../../models/locations-models/coordinate-interface';
import { FeedbackService } from '../../../../../core/feedback.service';
import { copyContentToClipboard } from '../../../../../utils/miscellaneous';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { NgClass } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { LetDirective } from '@ngrx/component';

@Component({
    selector: 'app-coordinates-editor',
    templateUrl: './coordinates-editor.component.html',
    styleUrls: ['../simple-text-editor/simple-text-editor.component.scss',
                './coordinates-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [LetDirective, FormsModule, CdkTrapFocus, ReactiveFormsModule, NgClass, MatFormField, MatLabel, MatInput, GxInputDirective, MatIcon, MatError, MatTooltip]
})
export class CoordinatesEditorComponent extends AgEditorComponent<Location>{

  @ViewChild('latitudeInput') latitudeInput: ElementRef;

  constructor(private fb: FormBuilder,
              protected feedbackService: FeedbackService,
              protected cd: ChangeDetectorRef,) {
    super(feedbackService, cd);
  }

  agInit(params: ICellEditorParams<Location>) {
    super.agInit(params);
    this.registerCoordinatesListener();
  }

  protected override createEditorFormGroup(params: ICellEditorParams<Location>):
      FormGroup<{latitude: FormControl<string>, longitude: FormControl<string>}> {
    return this.fb.group({
      latitude: [params?.data?.coordinateImport?.latitude ?
          String(params.data.coordinateImport.latitude) : '', LatitudeValidators
          .concat(params?.data?.coordinateImport?.longitude ? Validators.required : [])],
      longitude: [params?.data?.coordinateImport?.longitude ?
          String(params.data.coordinateImport.longitude) : '', LongitudeValidators
          .concat(params?.data?.coordinateImport?.latitude ? Validators.required : [])],
    })
  }

  /**
   * Return the new location field value
   * Override in child component if needed
   * @protected
   */
  protected override getNewFieldValue(): CoordinateInterface {
    return {
      latitude: this.editorFormGroup.get('latitude').value ?
          Number(this.editorFormGroup.get('latitude').value) : null,
      longitude: this.editorFormGroup.get('longitude').value ?
          Number(this.editorFormGroup.get('longitude').value) : null,
    };
  };

  getErrorMessage(control: AbstractControl<string>): string {
    const { errors } = control;

    const errorKeys: string[] = Object.keys(errors || []);

    // nothing to return...
    if (!errorKeys.length) {
      return null;
    }

    const firstErrorKey = errorKeys[0];

    switch (firstErrorKey) {
      case ValidationErrorKeys.REQUIRED:
        return 'Required field.';
      case ValidationErrorKeys.MIN_COORDINATE:
        return 'Below min. value (lat: -90, long: -180)';
      case ValidationErrorKeys.MAX_COORDINATE:
        return 'Above max. value (lat: 90, long: 180)';
      case ValidationErrorKeys.PATTERN_NUMERIC:
        return 'Not a number.';
      default:
        return 'Please check your input.';
    }
  }

  /**
   * Listens to Imported coordinates changes and sets validators dynamically + makes sure that
   * the decimal separator is always '.'
   * @private
   */
  private registerCoordinatesListener() {
    this.editorFormGroup?.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((val: {latitude: string, longitude: string}) => {
        const latitudeControl = this.editorFormGroup.get('latitude');
        const longitudeControl = this.editorFormGroup.get('longitude');
        // force '.' as the decimal separator
        // react only to user changes!
        latitudeControl.setValue(val?.latitude?.replace(/,/g, '.'), { emitEvent: false });
        // if the paired control has a value, set a 'required' validator as well the default ones
        latitudeControl.setValidators(LatitudeValidators.concat(val?.longitude ? Validators.required : []));
        // update the validity of the paired control
        latitudeControl.updateValueAndValidity({ emitEvent: false });

        longitudeControl.setValue(val?.longitude?.replace(/,/g, '.'), { emitEvent: false });
        longitudeControl.setValidators(LongitudeValidators.concat(val?.latitude ? Validators.required : []));
        longitudeControl.updateValueAndValidity({ emitEvent: false });
      });
  }

  /**
   * A hook to perform any necessary operation just after the GUI for this component has been
   * rendered on the screen.
   * This method is called each time the edit component is activated.
   * This is useful for any logic that requires attachment before executing, such as putting focus
   * on a particular DOM element.
   */
  afterGuiAttached(): void {
    this.latitudeInput?.nativeElement?.focus();
  }


  /**
   * Copy text input to clipboard.
   * @param input Textarea / Input reference
   */
  copyTextInput(input: HTMLTextAreaElement | HTMLInputElement) {
    copyContentToClipboard(input, (ok) => {
      ok && this.feedbackService.showSuccessSnackbar({
        data: { userFriendlyMessage: 'Copied' },
        isAddressListSnackbar: true,
      });
    });
  }


}
